import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  Button,
  Flex,
  Grid,
  GridItem,
  GridProps,
  useMediaQuery,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { PROJECTS_PAGE_SIZE } from '../constants/Values';
import { Project } from '../models/Project';
import { TextType } from '../models/Text';

import Spacer from './Spacer';
import SquareCard from './SquareCard';
import Text from './Text';

interface ProjectsGalleryWithPaginationProps {
  items: Project[];
  gap?: GridProps['gap'];
  buttonLabel?: string;
}

const ProjectsGalleryWithPagination = ({
  items,
  gap = {
    base: '12px',
    md: '12px',
    lg: '12px',
  },
  buttonLabel,
}: ProjectsGalleryWithPaginationProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [isMobile] = useMediaQuery(
    '(max-width: 767px)'
  );

  const [isLastOne, setIsLastOne] =
    useState(false);

  const [currentIndex, setCurrentIndex] =
    useState(
      Math.min(
        items.length,
        PROJECTS_PAGE_SIZE
      )
    );

  const itemsToDisplay = useMemo<Project[]>(() => {
    const orderedItems = [...items].reverse();

    return isMobile
      ? orderedItems.slice(0, currentIndex)
      : orderedItems;
  }, [items, isMobile, currentIndex]);

  const buttonDisabled = useMemo(
    () => (isMobile ? isLastOne : false),
    [isLastOne, isMobile]
  );

  const handleLoadMore = useCallback(() => {
    if (!isLastOne) {
      const diff =
        items.length -
        itemsToDisplay.length;

      const newIndex =
        currentIndex +
        Math.min(
          diff,
          PROJECTS_PAGE_SIZE
        );

      setCurrentIndex(newIndex);

      if (newIndex >= items.length) {
        setIsLastOne(true);
      }
    }
  }, [
    isLastOne,
    items.length,
    itemsToDisplay.length,
    currentIndex,
  ]);

  const handleGoBack = useCallback(() => {
    navigate('/');
  }, [navigate]);

  const handleProjectClick = useCallback(
    (id: number) => {
      navigate(`/projects/${id}`);
    },
    [navigate]
  );

  useEffect(() => {
    if (
      items.length <=
      PROJECTS_PAGE_SIZE
    ) {
      setIsLastOne(true);
    } else {
      setIsLastOne(false);
    }

    setCurrentIndex(
      Math.min(
        PROJECTS_PAGE_SIZE,
        items.length
      )
    );
  }, [items.length]);

  return (
    <>
      <Grid
        flex={1}
        width="100%"
        templateColumns={{
          base: 'repeat(1, 1fr)',
          md: 'repeat(2, 1fr)',
          lg: 'repeat(3, 1fr)',
        }}
        gap={gap}
      >
        {itemsToDisplay.map((project, index) => (
          <GridItem
            key={`item-${project.title}-${index}`}
            width="100%"
            aspectRatio={1}
            overflow="hidden"
          >
            <button
              type="button"
              onClick={() =>
                handleProjectClick(project.id)
              }
              style={{
                display: 'block',
                width: '100%',
                height: '100%',
                padding: 0,
                margin: 0,
                border: 0,
                background: 'transparent',
                cursor: 'pointer',
              }}
            >
              <SquareCard
                heroImage={project.heroImage}
                withShadow
                visibleNode={
                  <Flex
                    width="100%"
                    height="100%"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="flex-end"
                    paddingX={{
                      base: '20px',
                      md: '30px',
                      lg: '40px',
                    }}
                    paddingBottom="40px"
                    boxSizing="border-box"
                  >
                    <Text
                      type={TextType.Inter20Regular}
                      color="white"
                      others={{
                        textAlign: 'center',
                        width: '100%',
                      }}
                    >
                      {project.title.toUpperCase()}
                    </Text>
                  </Flex>
                }
              >
                <Flex
                  width="100%"
                  height="100%"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="flex-end"
                  paddingX={{
                    base: '20px',
                    md: '30px',
                    lg: '40px',
                  }}
                  paddingBottom="40px"
                  boxSizing="border-box"
                >
                  <Text
                    type={TextType.Inter20Regular}
                    color="white"
                    others={{
                      textAlign: 'center',
                      width: '100%',
                    }}
                  >
                    {project.title.toUpperCase()}
                  </Text>

                  <Spacer size={12} />

                  <Text
                    type={{
                      base: TextType.Inter12Light,
                      md: TextType.Inter14Light,
                    }}
                    color="white"
                    others={{
                      textAlign: 'center',
                      width: '100%',
                    }}
                  >
                    {project.shortDescription}
                  </Text>
                </Flex>
              </SquareCard>
            </button>
          </GridItem>
        ))}
      </Grid>

      <Button
        variant="solid"
        bgColor="primary"
        marginTop="30px"
        width={{
          base: '50%',
          md: '33.3333%',
        }}
        borderRadius={0}
        isDisabled={buttonDisabled}
        onClick={
          isMobile
            ? handleLoadMore
            : handleGoBack
        }
      >
        <Text
          type={{
            base: TextType.Inter12Bold,
            md: TextType.Inter14Bold,
          }}
          color="white"
        >
          {(
            buttonLabel ||
            t('homepage.projects.buttonLabel')
          ).toUpperCase()}
        </Text>
      </Button>
    </>
  );
};

export default ProjectsGalleryWithPagination;